---
title: Privacidade
updated: 2026-10-02
---

## 1. Controlador dos dados

O Draughts Game ("nós") é o controlador dos dados pessoais coletados por meio desta plataforma. Contato: [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com)

## 2. Dados que coletamos

### 2.1 Dados da conta

- **Nome de usuário** - escolhido por você, visível publicamente
- **Endereço de e-mail** - criptografado em repouso com AES-256-GCM, usado para identificar a conta, não visível publicamente
- **Senha** - guardada como hash bcrypt (fator de custo 12), nunca em texto puro
- **Confirmação de idade** - registramos que você confirmou ter pelo menos 16 anos no cadastro
- **Situação da verificação de e-mail** - se você confirmou o seu endereço de e-mail, além de tokens de curta duração de verificação e de redefinição de senha
- **Personalização do perfil (opcional)** - avatar e país, escolhidos por você, visíveis publicamente

### 2.2 Dados de jogo

- Lances, resultados e horários das partidas
- Rating e estatísticas dos jogadores
- Participação e resultados em torneios
- Tentativas nos problemas, rating de problemas e sequências de acertos
- Sua biblioteca de estudo: notas de posição (texto livre escrito por você), pastas, partidas salvas e posições salvas no editor — privadas, visíveis apenas para você

### 2.3 Dados técnicos

- Endereço IP (para fins de segurança e antitrapaça)
- String de user agent do navegador
- Tokens de sessão

### 2.4 Pedidos de suporte

Se você entrar em contato pelo formulário de suporte, coletamos o seu endereço de e-mail, um nome opcional e o assunto e o conteúdo da sua mensagem, para que possamos tratar e responder ao seu pedido.

## 3. Base legal do tratamento

Tratamos os seus dados pessoais com base em:

- **Execução de contrato** (art. 6.º, n.º 1, alínea b, do RGPD) - para prestar o serviço de jogo que você contratou, incluindo e-mails transacionais (verificação da conta, redefinição de senha) e respostas de suporte.
- **Interesses legítimos** (art. 6.º, n.º 1, alínea f, do RGPD) - para segurança da plataforma, antitrapaça, estatísticas agregadas sobre o jogo (quais variantes são jogadas, como as partidas terminam) e melhoria do serviço.
- **Consentimento** (art. 6.º, n.º 1, alínea a, do RGPD) - para a medição de uso via Umami e o rastreamento de erros via Sentry. Nenhum dos dois é ativado até você aceitar no aviso.

## 4. Como usamos os seus dados

- Fornecer e operar a plataforma de jogo
- Autenticar a sua conta e manter as sessões
- Calcular e exibir o rating dos jogadores e os rankings
- Fazer o pareamento e organizar torneios
- Detectar e prevenir trapaça e abusos
- Enviar e-mails transacionais (verificação da conta, redefinição de senha)
- Tratar e responder aos seus pedidos de suporte
- Melhorar o serviço por meio de análises agregadas e anônimas
- Analisar dados de jogo (por exemplo, variantes jogadas e resultados) de forma agregada e anonimizada, para entender o uso e melhorar a plataforma

## 5. Cookies e serviços de terceiros

Usamos cookies essenciais para a autenticação e para as suas preferências. Contamos com os seguintes serviços:

- **Umami** (análise) - hospedado por nós mesmos, na nossa infraestrutura na UE. Não usa cookies e só é ativado se você aceitar a análise no aviso. As visitas são identificadas por uma impressão pseudonimizada, derivada do seu endereço IP e do navegador e renovada todos os dias; os números são usados apenas de forma agregada e não rastreamos você entre sites.
- **Sentry** (rastreamento de erros) - ativado só com o seu consentimento.
- **Resend** (entrega de e-mails) - entrega os nossos e-mails transacionais (verificação da conta, redefinição de senha) e as notificações de suporte.
- **DigitalOcean** (hospedagem) - data center na UE (Frankfurt).

Não vendemos os seus dados nem usamos rastreadores de publicidade.

## 6. Retenção dos dados

- **Dados da conta** - guardados enquanto a sua conta estiver ativa.
- **Contas de convidado** - excluídas automaticamente depois de 30 dias sem uso.
- **Contas não verificadas** - se você se cadastrar mas nunca confirmar seu endereço de e-mail, a conta é excluída automaticamente depois de 30 dias sem uso.
- **Refresh tokens** - expiram depois de 7 dias (90 dias nas contas de convidado, para você reencontrar as suas partidas sem senha) e são limpos automaticamente a cada hora. O endereço IP e os dados do navegador ligados a uma sessão são apagados depois de 7 dias, mesmo quando a sessão dura mais.
- **Tokens de verificação de e-mail** - expiram depois de 24 horas.
- **Tokens de redefinição de senha** - expiram depois de 1 hora.
- **Pedidos de suporte** - guardados pelo tempo necessário para tratar o seu pedido.
- **Denúncias de abuso** - quando um jogador denuncia alguém por abuso no bate-papo, o bate-papo daquela partida (até 100 mensagens, dos dois jogadores) é guardado para moderação e excluído automaticamente depois de 90 dias. Fora isso, o bate-papo nunca é guardado.
- **Dados de jogo** - guardados por tempo indeterminado, como parte do registro público das partidas.
- **Biblioteca de estudo** - notas de posição, pastas, partidas salvas e posições salvas são guardadas até você excluí-las e, em todo caso, removidas com a exclusão da conta.
- **Dados de erro do Sentry** - excluídos automaticamente depois de 90 dias.
- **Contas excluídas** - os dados pessoais são removidos imediatamente na exclusão. Os registros das partidas são anonimizados (a referência ao jogador é removida).

## 7. Os seus direitos (RGPD)

Nos termos do Regulamento Geral sobre a Proteção de Dados (RGPD), você tem direito a:

- **Acesso** (art. 15.º) - baixar uma cópia dos seus dados pessoais nas configurações do perfil ("Exportar meus dados").
- **Retificação** (art. 16.º) - trocar a sua senha nas configurações do perfil. Para outras correções, entre em contato conosco.
- **Apagamento** (art. 17.º) - excluir a sua conta nas configurações do perfil ("Excluir a conta"). É imediato e irreversível.
- **Portabilidade dos dados** (art. 20.º) - exportar os seus dados em formato JSON legível por máquina, nas configurações do perfil.
- **Limitação** (art. 18.º) - pedir a limitação do tratamento entrando em contato conosco.
- **Oposição** (art. 21.º) - recusar a análise no aviso de cookies desliga **tanto** a medição de uso do Umami **quanto** o rastreamento de erros do Sentry. Você pode mudar de ideia a qualquer momento pelo interruptor nas configurações do perfil.

Para exercer qualquer um desses direitos, use as ferramentas de autoatendimento nas configurações do perfil ou escreva para [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com). Responderemos em até 30 dias.

## 8. Segurança dos dados

Adotamos medidas técnicas e organizacionais adequadas para proteger os seus dados pessoais, incluindo criptografia dos dados sensíveis em repouso e em trânsito, hashing seguro de senhas e limitação de taxa nos endpoints de autenticação.

## 9. Transferências internacionais

Os seus dados são guardados e tratados principalmente dentro do Espaço Econômico Europeu (EEE), no data center da DigitalOcean em Frankfurt. Os dados de erro do Sentry podem ser transferidos para os EUA ao abrigo da decisão de adequação do EU-US Data Privacy Framework (julho de 2023). Você pode recusar o Sentry nas configurações do perfil. Os e-mails transacionais são entregues pela Resend (EUA); essa transferência está coberta por um acordo de tratamento de dados com Cláusulas Contratuais-Tipo.

## 10. Privacidade de crianças

O Serviço não se destina a crianças com menos de 16 anos (art. 8.º do RGPD). Exigimos a confirmação de idade no cadastro. Não coletamos conscientemente dados pessoais de crianças com menos de 16 anos. Se descobrirmos que coletamos dados de uma criança com menos de 16 anos, vamos excluí-los prontamente.

## 11. Alterações nesta política

Podemos atualizar esta Política de Privacidade de tempos em tempos. Avisaremos os usuários cadastrados sobre mudanças relevantes por meio de um aviso na plataforma. A data de "Última atualização" no topo indica a revisão mais recente.

## 12. Autoridade de controle

Você tem o direito de apresentar reclamação à autoridade de controle competente em matéria de proteção de dados se acreditar que os seus direitos foram violados.

## 13. Contato

Para dúvidas sobre esta Política de Privacidade:

- E-mail: [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com)
